import { Rocket, Users, MailPlus, LineChart } from "lucide-react";

export default function Hero() {
  return (
    <section
      data-testid="wc-hero"
      className="relative overflow-hidden bg-[#30265b] text-white pt-32 sm:pt-40 pb-24 sm:pb-32 wc-grain"
    >
      {/* Decorative blur blobs */}
      <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#673dfc]/40 blur-3xl" />
      <div className="absolute -bottom-40 -right-24 w-[520px] h-[520px] rounded-full bg-[#d3fc3a]/10 blur-3xl" />
      <div className="absolute top-1/3 right-1/4 w-64 h-64 rounded-full bg-[#c5b4ff]/20 blur-3xl" />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 text-center">
        <h1 className="font-display font-extrabold text-[2.5rem] sm:text-5xl lg:text-[46px] xl:text-[52px] leading-[1.06] tracking-[-0.03em] max-w-[88rem] mx-auto">
          Build your{" "}
          <span className="text-[#d3fc3a]">$10K/month</span>
          <br className="hidden sm:block" />{" "}
          social media membership business
          <br className="hidden sm:block" />{" "}
          <span className="text-[#c5b4ff]">
            without doing all the marketing yourself.
          </span>
        </h1>

        <p className="mt-7 text-base sm:text-lg lg:text-xl text-white/75 max-w-2xl mx-auto leading-relaxed">
          Imagine having a dedicated marketing team creating your content,
          generating qualified leads, and even helping you convert prospects
          into subscribers — so you can focus on relationships and growing
          your recurring income.
        </p>

        <div className="mt-10 flex flex-col items-center gap-4">
          <a
            href="#pricing"
            data-testid="wc-hero-primary-cta"
            className="group bg-[#d3fc3a] text-[#1a1a24] font-display font-semibold text-base sm:text-lg rounded-full px-8 sm:px-10 py-4 sm:py-5 flex items-center gap-2 hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#d3fc3a]/25 transition-transform duration-300"
          >
            <Rocket size={18} /> Yes, Help Me Build My $10K/Month Business
          </a>
          <p className="text-sm sm:text-base text-white/60 font-nav">
            Your dedicated marketing team.
            <br className="sm:hidden" />
            <span className="hidden sm:inline"> </span>
            More leads. More conversations. More subscribers.
          </p>
        </div>

        {/* Value strip — three benefits, expanded */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5 text-left max-w-5xl mx-auto">
          {[
            {
              icon: Users,
              title: "Dedicated Marketing Team",
              subtitle:
                "Your marketing team working behind your business every month.",
              body: "Stop trying to do everything yourself. Our dedicated marketing team works behind the scenes creating your content, managing your marketing, and helping keep your business consistently visible — so you can spend more time building relationships and gaining new subscribers.",
              highlight: "More time. Less stress. More business growth.",
            },
            {
              icon: MailPlus,
              title: "Qualified Leads",
              subtitle: "Consistent lead generation delivered to you.",
              body: "No more wondering where your next lead will come from. We research qualified prospects, launch personalized outreach campaigns, and deliver warm opportunities directly to you — allowing you to focus on meaningful conversations instead of chasing leads.",
              highlight:
                "More conversations. More opportunities. More subscribers.",
            },
            {
              icon: LineChart,
              title: "Monthly Growth Review",
              subtitle: "Continuous improvement for better results.",
              body: "Every month, we'll review your marketing performance, outreach activity, and growth opportunities to help improve your results over time. You'll always know what's working, what can be improved, and where to focus next.",
              highlight:
                "Clear direction. Better decisions. Continuous growth.",
            },
          ].map((c) => (
            <div
              key={c.title}
              className="rounded-3xl bg-white/[0.04] border border-white/10 p-6 backdrop-blur-sm hover:border-[#d3fc3a]/40 transition-colors flex flex-col"
            >
              <div className="w-11 h-11 rounded-2xl bg-[#d3fc3a] text-[#1a1a24] grid place-items-center mb-4">
                <c.icon size={20} />
              </div>
              <div className="font-display font-bold text-lg text-white leading-tight">
                {c.title}
              </div>
              <div className="mt-1 font-nav text-sm text-[#c5b4ff]">
                {c.subtitle}
              </div>
              <p className="mt-4 text-sm text-white/75 leading-relaxed flex-1">
                {c.body}
              </p>
              <div className="mt-5 pt-4 border-t border-white/10 flex items-start gap-2 text-sm">
                <span className="text-[#d3fc3a] font-bold">✓</span>
                <span className="text-white/90 font-nav font-medium">
                  {c.highlight}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
